import React, { useState, useContext, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { ResQComplaintsViewModel } from '../ViewModels/ResQComplaintsViewModel';
import Button from '@mui/material/Button';
import { MsalContext } from '../Contexts/MsalContext';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ComplaintResponseViewModel from './ViewModel/ComplaintResponseViewModel';
import { formatDate } from '../Utilities/UtilityMethods';

const styles = {
    root: {
        color: 'black',
    },
    input: {
        color: 'black',
    },
    outerGridItem: {
        backgroundColor: '#fff',
    },
    botMargin: {
        marginBottom: (theme: Theme) => theme.spacing(1)
    }
}

interface IActionsProps {
    action: ComplaintResponseViewModel,
    getComplaints: () => void;
}

export default function Action({ action, getComplaints }: IActionsProps) {
    const { msalFetch } = useContext(MsalContext);
    const [loaded, setLoaded] = useState(false);
    const [editedComplaint, setEditedComplaint] = useState<ResQComplaintsViewModel>(action.complaint);

    useEffect(() => {
        setLoaded(true);
    }, [action]);

    const handleChange = (e) => {
        setEditedComplaint({ ...editedComplaint, [e.target.name]: e.target.value });
    }

    const saveClick = () => {
        msalFetch('api/IssueReview/UpdateComplaint', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
            },
            body: JSON.stringify(editedComplaint)
        })
            .then(() => {
                getComplaints()
            })
    }

    const deleteClick = () => {
        msalFetch('api/IssueReview/RemoveComplaintByID?ID=' + encodeURIComponent(action.complaint.id))
            .then(() => getComplaints());
    }

    const isComplaintEditable = (complaint: ResQComplaintsViewModel) => {
        return complaint.originator !== 'Q-AI'
            && complaint.issueType !== 'Redress'
    }

    return (loaded ?
        <Grid key={action.complaint.id} sx={styles.outerGridItem}>
            <Grid container spacing={1}>
                <Grid size={6}>
                    {
                        action.complaint.originator !== 'Q-AI' &&
                        <Select
                            name='issueType'
                            fullWidth
                            value={editedComplaint.issueType}
                            disabled={!isComplaintEditable(action.complaint)}
                            onChange={handleChange}
                        >
                            {action.issueTypes.map((item, i) =>
                                <MenuItem key={i} value={item}>{item}</MenuItem>
                            )}
                        </Select>
                    }
                </Grid>
                <Grid size={6}>
                    {
                        action.complaint.originator !== 'Q-AI' &&
                        <Select
                            name='issueCategory'
                            fullWidth
                            value={editedComplaint.issueCategory}
                            disabled={!isComplaintEditable(action.complaint)}
                            onChange={handleChange}
                        >
                            {action.issueCategories.map(category =>
                                <MenuItem key={category} value={category}>{category}</MenuItem>
                            )}
                        </Select>
                    }
                </Grid>
                {
                    action.complaint.originator === 'Q-AI' &&
                    <>
                        <Grid size={6}>
                            <Typography>Issue Type:</Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography>{action.complaint.issueType}</Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography>Issue Category:</Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography>{action.complaint.issueCategory}</Typography>
                        </Grid>
                    </>
                }
                <Grid size={6}>
                    <Typography>Contact Name:</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>{action.complaint.contactName}</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>Submitted By:</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>{action.complaint.originator}</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>Submitted At:</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>{formatDate(action.complaint.insertstamp)}</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>Outcome Set By:</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>{action.complaint.authorizer}</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>Outcome Set At:</Typography>
                </Grid>
                <Grid size={6}>
                    <Typography>
                        {action.complaint.authorizeTimeStamp
                            ? formatDate(action.complaint.authorizeTimeStamp)
                            : ''}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <TextField label="Notes" multiline fullWidth value={action.complaint.notes} onChange={handleChange} name='notes' />
                </Grid>
                <Grid size={12}>
                    <Select
                        name='outcome'
                        fullWidth
                        value={editedComplaint.outcome}
                        onChange={handleChange}
                    >
                        {action.outcomes.map((item, i) =>
                            <MenuItem key={i} value={item}>{item}</MenuItem>
                        )}
                    </Select>
                </Grid>
                <Grid size={6}>
                    <Button fullWidth variant="outlined" color="primary" onClick={deleteClick}>Delete</Button>
                </Grid>
                <Grid size={6}>
                    <Button fullWidth variant="outlined" color="secondary" onClick={saveClick}>Save</Button>
                </Grid>
            </Grid>
        </Grid >
        :
        <React.Fragment />
    )
}